/**
 * PDP gallery wishlist heart.
 *
 * Swym's own button stays the single source of truth (so items land in the default list
 * its app reads, and its login / email-capture flow still runs). Rather than proxying
 * clicks, Swym's button is physically MOVED into .pdp-wishlist-slot and stacked
 * transparently on top of the heart, so the shopper's real click lands on Swym directly.
 *
 * Why not forward clicks with btn.click()? Verified against this store: Swym ignores
 * synthetic/untrusted events entirely. Only a genuine user gesture works.
 *
 * State (including on page load, for an already-favorited item) comes from
 * _swat.isInWishlist(product, cb) — Swym's own data, keyed on variant id. That is
 * deliberately preferred over sniffing Swym's button CSS classes, whose tokens vary by
 * app version. The button's classes are still watched as a fast optimistic signal so
 * the heart reacts immediately on click, before the API round-trip settles.
 */
(function () {
  var SLOT = '.pdp-wishlist-slot';
  var HEART = '[data-wishlist-action]';
  var BAR = '.swym-button-bar';
  var SWYM_BTN = '.swym-add-to-wishlist, .swym-button';
  var VARIANT_SELECT = 'select[name="id"]';

  // Two slots can exist at once (desktop gallery + mobile swiper's first slide, see
  // main-product.liquid) with only one visible per breakpoint via CSS. currentSlot
  // tracks which one actually hosts the real button, so a breakpoint change (resize/
  // rotate) can move it rather than leaving it stuck in a now-hidden slot.
  var currentSlot = null;
  var observerAttached = false;

  function isVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function slot() {
    var candidates = document.querySelectorAll(SLOT);
    for (var i = 0; i < candidates.length; i++) {
      if (isVisible(candidates[i])) return candidates[i];
    }
    // Nothing visible yet (e.g. called before layout settles) — fall back to the first.
    return candidates[0] || null;
  }

  function swymButton() {
    var s = slot();
    return s ? s.querySelector(SWYM_BTN) : null;
  }

  /** Currently selected variant, so the heart reflects per-variant wishlist state. */
  function currentVariantId() {
    var sel = document.querySelector(VARIANT_SELECT);
    if (sel && sel.value) return Number(sel.value);
    var heart = document.querySelector(HEART);
    return heart ? Number(heart.dataset.wishlistVariantId) : null;
  }

  /** Swym product payload for the selected variant, falling back to the page product. */
  function swymProduct() {
    var vid = currentVariantId();
    if (vid && window.SwymProductVariants && window.SwymProductVariants[vid]) {
      return window.SwymProductVariants[vid];
    }
    return window.SwymPageData;
  }

  /** Optimistic signal only — token varies by Swym version, so never the sole source. */
  function buttonLooksAdded() {
    var btn = swymButton();
    if (!btn) return false;
    var cls = (btn.className || '').toString();
    return /swym-added|swym-loaded-added|added-to-wishlist|swym-remove/i.test(cls) ||
      btn.getAttribute('aria-pressed') === 'true';
  }

  /**
   * Paint the heart. Each write is guarded by a read: setAttribute/classList emit
   * mutation records even when the value is unchanged, which would feed the observer
   * below and spin. Keep this idempotent.
   */
  function applyState(added) {
    document.querySelectorAll(HEART).forEach(function (heart) {
      if (heart.classList.contains('is-wishlisted') !== added) {
        heart.classList.toggle('is-wishlisted', added);
      }
    });
    var btn = swymButton();
    if (btn) {
      var label = added ? 'Remove from wishlist' : 'Add to wishlist';
      if (btn.getAttribute('aria-label') !== label) btn.setAttribute('aria-label', label);
    }
  }

  /** Authoritative check against Swym's own data. Safe to call before Swym has loaded. */
  function refreshFromSwym() {
    if (!window._swat || typeof _swat.isInWishlist !== 'function') return;
    var product = swymProduct();
    if (!product || !product.epi) return;
    try {
      _swat.isInWishlist(product, function (inList) {
        applyState(!!inList);
      });
    } catch (e) { /* fall back to the optimistic class signal */ }
  }

  /**
   * Move Swym's button bar into the currently-visible gallery slot. Safe to call
   * repeatedly: no-ops once the button is already in the right place, and re-parenting
   * an already-adopted button (breakpoint change) just moves the live node — its
   * listeners/observer stay bound to the node itself, so they don't need reattaching.
   */
  function adoptSwymButton() {
    var s = slot();
    if (!s) return false;
    if (currentSlot === s) return true;

    var bar = document.querySelector(BAR);
    if (!bar || !bar.querySelector(SWYM_BTN)) return false;

    s.appendChild(bar);
    currentSlot = s;

    if (!observerAttached) {
      observerAttached = true;
      // Optimistic: reflect Swym's own button changes instantly, then confirm via API.
      new MutationObserver(function () {
        if (buttonLooksAdded()) applyState(true);
        refreshFromSwym();
      }).observe(bar, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-pressed'],
      });

      // A real click lands on Swym's button; re-confirm state as its request settles.
      bar.addEventListener('click', function () {
        [400, 1200, 2500].forEach(function (d) { setTimeout(refreshFromSwym, d); });
      });
    }

    refreshFromSwym();
    return true;
  }

  // Swym injects (and re-renders) its button asynchronously, and only after cookie
  // consent. childList-only — our own class writes on the heart are attribute changes,
  // so they can never re-trigger this observer.
  new MutationObserver(function () {
    adoptSwymButton();
  }).observe(document.documentElement, { subtree: true, childList: true });

  // Crossing the mobile/desktop breakpoint (resize, rotate, devtools) changes which
  // slot is visible; move the button to match.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(adoptSwymButton, 200);
  });

  // Wishlist state is per-variant, so re-check when the shopper switches variant.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches(VARIANT_SELECT)) refreshFromSwym();
  });

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    adoptSwymButton();
    refreshFromSwym();
    // Swym hydrates its wishlist data slightly after load/auth.
    [500, 1500, 3000].forEach(function (d) {
      setTimeout(function () { adoptSwymButton(); refreshFromSwym(); }, d);
    });
    try {
      swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, refreshFromSwym);
      swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, refreshFromSwym);
    } catch (e) { /* event layer shape varies by version; observers already cover us */ }
  });

  document.addEventListener('DOMContentLoaded', function () {
    adoptSwymButton();
    refreshFromSwym();
  });

  adoptSwymButton();
  refreshFromSwym();
})();
